<script setup lang="ts">
import ButtonMenu from '@/assets/icons/Buttons/ButtonMenu.vue';
import FileSource from '@/components/BaseComponents/FileSource.vue';
import ImportMenu from '@/components/Menus/ImportMenu.vue';
import MenuImportingSkeleton from '@/components/Menus/MenuImportingSkeleton.vue';
import MyRecipesMenuItem from '@/components/MyRecipes/MyRecipesMenuItem.vue';
import { useFileUpload, type UploadOptions } from '@/composables/useFileUpload';
import { useMenus } from '@/composables/useMenus';
import { usePermissions } from '@/composables/usePermissions';
import type { MessageSchema } from '@/i18n';
import { useUserStore } from '@/stores/useUserStore';
import type { CameraUploadPayload } from '@/types/camera';
import type { CreateMenuRequest, MenuImportResponse } from '@/types/menu';
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Menu from 'primevue/menu';
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

const { t } = useI18n<{ message: MessageSchema }>({ useScope: 'global' });
const { can } = usePermissions();
const router = useRouter();
const { uploadFile, isUploading: isFileUploading } = useFileUpload();

const {
  menus,
  isLoading,
  isImporting,
  fetchMenus,
  createMenu,
  // Remove the old importMenu function since we'll handle it directly
} = useMenus();

// Dialog states
const showCreateMenuDialog = ref(false);
const showImportMenuDialog = ref(false);
const newMenuForm = ref<CreateMenuRequest>({ name: '' });
const menuOptions = ref();

// Transform menus into menu items format
const menuItems = computed(() => {
  return [...menus.value]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((menu) => ({
      name: menu.name,
      icon: null,
      url: `/my-recipes/my-menus/${menu.id}`,
      menu: menu,
    }));
});

const menuOptionsItems = computed(() => [
  {
    items: [
      {
        label: t('common.create'),
        icon: 'pi pi-plus',
        command: () => {
          showCreateMenuDialog.value = true;
        },
      },
    ],
  },
]);

onMounted(async () => {
  await fetchMenus();
});

const handleCreateMenu = async () => {
  if (!newMenuForm.value.name.trim() || !can('menus', 'create')) return;

  const result = await createMenu(newMenuForm.value);
  if (result) {
    showCreateMenuDialog.value = false;
    newMenuForm.value = { name: '' };
  }
};

const handleImportMenu = async (payload: CameraUploadPayload) => {
  if (!can('menus', 'create')) return;

  const userStore = useUserStore();
  if (!userStore.validateUserCanGenerate()) return;

  showImportMenuDialog.value = false;

  const uploadOptions: UploadOptions<MenuImportResponse> = {
    endpoint: '/menus/import',
    fieldName: 'file',
    transport: 'json-base64', // Force base64 for consistent processing
    onSuccess: (data: MenuImportResponse) => {
      // Refresh menus list
      fetchMenus();

      // Navigate to the newly created menu details
      if (data.id) {
        router.push({ path: `/my-recipes/my-menus/${data.id}` });
      }
    },
    onError: (error) => {
      console.error('Menu import failed:', error);
    },
  };

  uploadFile<MenuImportResponse>(payload, uploadOptions);
};

const toggleMenuOptions = (event: Event) => {
  if (!can('menus', 'create')) return;
  menuOptions.value.toggle(event);
};

const handleShowImportDialog = () => {
  if (!can('menus', 'create')) return;
  showImportMenuDialog.value = true;
};

// Combined loading state
const isBusy = computed(() => isLoading.value || isImporting.value || isFileUploading.value);
</script>

<template>
  <main>
    <!-- Show importing skeleton when importing -->
    <MenuImportingSkeleton v-if="isBusy" />

    <!-- Show normal content when not importing -->
    <div v-else>
      <div class="mb-7 flex items-center justify-between gap-2">
        <h1 class="text-2xl">{{ t('pageTitle.myMenus') }}</h1>
        <Menu
          v-if="can('menus', 'create')"
          id="overlay_menu"
          ref="menuOptions"
          :model="menuOptionsItems"
          :popup="true"
          :pt="{ submenuHeader: { class: '!p-0' } }"
          :pt-options="{ mergeProps: true }"
        />
        <Button
          v-if="can('menus', 'create')"
          link
          rounded
          :pt="{ root: { class: '!p-1.5 !w-8 !h-8' } }"
          :pt-options="{ mergeProps: true }"
          @click="toggleMenuOptions"
        >
          <template #icon>
            <ButtonMenu />
          </template>
        </Button>
      </div>

      <div class="mt-4">
        <p v-if="isLoading" class="mt-4 italic">{{ t('common.loading') }}</p>
        <p v-else-if="!isLoading && menuItems.length === 0" class="mt-4 italic">
          {{ t('menu.noMenus') }}
        </p>
        <MyRecipesMenuItem
          v-for="(item, index) in menuItems"
          v-else
          :key="item.name"
          :name="item.name"
          :icon="item.icon ?? undefined"
          :url="item.url"
          :index="index"
        />
      </div>

      <div v-if="can('menus', 'create')" class="mt-4">
        <p class="inline-button mb-10" @click="showCreateMenuDialog = true">+ {{ t('menu.create.label') }}</p>
      </div>
    </div>

    <!-- Create Menu Dialog -->
    <Dialog v-model:visible="showCreateMenuDialog" modal :header="t('menu.create.label')" :style="{ width: '25rem', maxWidth: '90vw' }">
      <div v-if="showCreateMenuDialog" class="mb-4 flex items-center gap-4">
        <label for="menuName" class="w-28 font-semibold">{{ t('menu.form.name.label') }}</label>
        <InputText id="menuName" v-model="newMenuForm.name" class="flex-auto" autocomplete="off" />
      </div>
      <div class="flex justify-between gap-2">
        <Button type="button" :label="t('common.cancel')" outlined :disabled="false" @click="showCreateMenuDialog = false" />
        <div class="flex justify-end gap-2">
          <Button type="button" :label="t('common.save')" :disabled="!newMenuForm.name.trim()" @click="handleCreateMenu" />
        </div>
      </div>
    </Dialog>

    <!-- Import Menu Dialog -->
    <FileSource
      v-model:visible="showImportMenuDialog"
      :header="t('menu.import.chooseSource')"
      camera-text="Camera"
      gallery-text="Gallery / PDF"
      :camera-accept="'image/*'"
      :gallery-accept="'image/*,application/pdf'"
      @file-uploaded="handleImportMenu"
    />

    <!-- Import Menu Button -->
    <ImportMenu v-if="can('menus', 'create') && !isBusy" @show-import-dialog="handleShowImportDialog" />
  </main>
</template>
